import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { Badge, Button } from '../atoms';
import { useToast } from './Toast';
import {
  MAIL_CONNECTION_KEY, isConnected, mailErrorMessage, startOutlookConnect, useMailConnection,
} from '../lib/mail';

/**
 * MailConnectionCard — the signed-in user's own Outlook link (spec 0014, AC-1 / AC-2).
 *
 * Connect opens Microsoft's own sign-in; the link counts as connected only once appealstack has
 * read the mailbox profile. A link whose token refresh failed shows its reason and Reconnect.
 * Unlink deletes appealstack's copy of the grant.
 *
 * `returnTo` is the page to come back to after Microsoft (the composer passes its own record
 * page so the saved draft is waiting there).
 */
export interface MailConnectionCardProps {
  returnTo?: string;
  compact?: boolean;
}

export function MailConnectionCard({ returnTo, compact = false }: MailConnectionCardProps) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const q = useMailConnection();
  const [busy, setBusy] = useState(false);
  const state = q.data;
  const conn = state?.connection ?? null;

  async function connect() {
    setBusy(true);
    try { await startOutlookConnect(returnTo); } catch (err) {
      toast({ variant: 'error', message: mailErrorMessage(err) });
      setBusy(false);
    }
  }
  async function unlink() {
    setBusy(true);
    try {
      await api.delete('/api/mail-connections/me');
      await queryClient.invalidateQueries({ queryKey: MAIL_CONNECTION_KEY });
      toast({ variant: 'info', message: 'Outlook unlinked. Drafts you already pushed stay in your mailbox.' });
    } catch (err) {
      toast({ variant: 'error', message: mailErrorMessage(err) });
    } finally { setBusy(false); }
  }

  const hint = { fontSize: 'var(--fs-sm)', color: 'var(--color-ink-3)', margin: 0 } as const;

  return (
    <div data-testid="mail-connection-card" style={{ border: '1px solid var(--color-border)', borderRadius: 'var(--r-lg)', padding: compact ? 'var(--sp-3)' : 'var(--sp-4)', background: 'var(--color-card)', display: 'grid', gap: 'var(--sp-2)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-2)', flexWrap: 'wrap' }}>
        <strong style={{ fontSize: 'var(--fs-md)' }}>Microsoft Outlook</strong>
        {conn?.status === 'connected' && <Badge variant="success">Connected</Badge>}
        {conn?.status === 'error' && <Badge variant="danger">Needs reconnecting</Badge>}
        {!conn && state?.configured && <Badge variant="neutral">Not connected</Badge>}
        {state && !state.configured && <Badge variant="neutral">Not available</Badge>}
      </div>

      {q.isLoading && <p style={hint}>Checking your connection…</p>}
      {state && !state.configured && (
        <p style={hint}>Your firm has not set up the Outlook connector yet. Email you write here can still be saved on the record.</p>
      )}
      {state?.configured && !conn && (
        <p style={hint}>Link your own Microsoft account so the email you write here lands in your Outlook Drafts, ready for you to review and send. appealstack never sends it for you.</p>
      )}
      {conn && (
        <p style={hint}>
          {conn.account_email ?? 'Your mailbox'}
          {conn.connected_at ? ` · linked ${new Date(conn.connected_at).toLocaleDateString()}` : ''}
        </p>
      )}
      {conn?.status === 'error' && conn.last_error && (
        <p role="alert" style={{ ...hint, color: 'var(--color-status-red)' }}>{conn.last_error}. Reconnect to keep pushing drafts.</p>
      )}

      {state?.configured && (
        <div style={{ display: 'flex', gap: 'var(--sp-2)', flexWrap: 'wrap' }}>
          {!conn && <Button variant="primary" size="sm" onClick={connect} loading={busy}>Connect Outlook</Button>}
          {conn && !isConnected(state) && <Button variant="primary" size="sm" onClick={connect} loading={busy}>Reconnect</Button>}
          {conn && <Button variant="secondary" size="sm" onClick={unlink} disabled={busy}>Unlink</Button>}
        </div>
      )}
    </div>
  );
}
